import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useMatter } from "../context/MatterContext";
import { useModules } from "../context/ModulesContext";
import { disablePortalModule, fetchSurveyStatus } from "../helpers/apiHelper";
import {
  findActiveMariana2026RetainerModuleForMatter,
  getModuleContext,
} from "../modules/moduleUtils";
import FullscreenLoader from "./FullscreenLoader";
import PortalSurveyViewer from "./PortalSurveyViewer";
import "../styles/Mariana2026RetainerGate.css";

const getAuthFlow = () => {
  return (
    localStorage.getItem("authFlow") || sessionStorage.getItem("authFlow") || ""
  );
};

const getAccessToken = (tokens) => {
  return (
    tokens?.accessToken ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken") ||
    ""
  );
};

const isSurveyCompleted = (status) => {
  return (
    status?.questionnaireCompleted === true ||
    ["COMPLETED", "CLOSED"].includes(
      String(status?.invitationStatus || "").toUpperCase(),
    )
  );
};

const isSurveyClosed = (status) => {
  return String(status?.invitationStatus || "").toUpperCase() === "CLOSED";
};

export default function Mariana2026RetainerGate({ children }) {
  const { isLoggedIn, loading, tokens } = useAuth();
  const { party, matter, bootstrap, bootstrapping, bootstrapState } = useMatter();
  const { modules, modulesInitialized, refreshModules } = useModules();
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerModule, setViewerModule] = useState(null);
  const [viewerClosing, setViewerClosing] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const disabledClosedInvitationsRef = useRef(new Set());

  const selectedMatterId = matter?.id || matter?.matterId || "";
  const isPortalUserFlow = getAuthFlow() === "portal_user";
  const accessToken = getAccessToken(tokens);

  useEffect(() => {
    if (
      loading ||
      !isLoggedIn ||
      !isPortalUserFlow ||
      modulesInitialized ||
      bootstrapping ||
      // Only kick off the first bootstrap. Once it has completed, modules are initialised from its
      // payload; re-running it while that is still in progress loops /me -> /modules -> status.
      bootstrapState !== "idle"
    ) {
      return;
    }

    bootstrap?.();
  }, [
    loading,
    isLoggedIn,
    isPortalUserFlow,
    modulesInitialized,
    bootstrapping,
    bootstrapState,
    bootstrap,
  ]);

  const activeModule = useMemo(() => {
    return findActiveMariana2026RetainerModuleForMatter(modules, selectedMatterId);
  }, [modules, selectedMatterId]);

  const moduleContext = getModuleContext(activeModule);
  const moduleId = activeModule?.id || "";
  const moduleInstanceKey = activeModule?.instanceKey || moduleContext?.matterId || "";
  const invitationId = moduleContext?.invitationId || "";
  const invitationLink = moduleContext?.invitationLink || moduleContext?.link || "";

  const syncSurveyStatus = useCallback(async () => {
    if (!activeModule || !invitationId || !accessToken) {
      setStatus(null);
      return null;
    }

    try {
      setStatusLoading(true);
      const nextStatus = await fetchSurveyStatus(accessToken, invitationId);
      setStatus(nextStatus);

      if (
        isSurveyClosed(nextStatus) &&
        !disabledClosedInvitationsRef.current.has(invitationId)
      ) {
        disabledClosedInvitationsRef.current.add(invitationId);
        await disablePortalModule(accessToken, {
          ...(moduleId ? { moduleId } : {}),
          module: "mariana2026_retainer",
          instanceKey: moduleInstanceKey,
        });
        await refreshModules();
      }

      return nextStatus;
    } catch (error) {
      console.error("Failed to refresh Mariana2026 retainer status:", error);
      disabledClosedInvitationsRef.current.delete(invitationId);
      setStatus(null);
      return null;
    } finally {
      setStatusLoading(false);
    }
  }, [activeModule, invitationId, accessToken, refreshModules, moduleId, moduleInstanceKey]);

  useEffect(() => {
    if (!activeModule) {
      setStatus(null);
      setViewerOpen(false);
      setViewerModule(null);
      return;
    }

    if (!invitationId) {
      return;
    }

    if (viewerOpen) {
      return;
    }

    syncSurveyStatus();
  }, [activeModule, invitationId, syncSurveyStatus, viewerOpen]);

  const completed = isSurveyCompleted(status);
  const shouldDeferGateUntilMatterSelection =
    isPortalUserFlow &&
    modulesInitialized &&
    !bootstrapping &&
    bootstrapState === "ready" &&
    !selectedMatterId;
  const shouldWaitForModules =
    isLoggedIn &&
    !loading &&
    !modulesInitialized &&
    bootstrapState !== "error" &&
    bootstrapState !== "no_open_matter" &&
    (isPortalUserFlow ? bootstrapping || !party : true);

  if (shouldWaitForModules || (activeModule && statusLoading && !status)) {
    return (
      <FullscreenLoader
        title="Carregando"
        message="Estamos verificando o acesso ao portal."
      />
    );
  }

  if (shouldDeferGateUntilMatterSelection) {
    return children;
  }

  if (!activeModule || completed) {
    return children;
  }

  const handleOpenQuestionnaire = () => {
    if (!invitationLink) return;
    setViewerModule(activeModule);
    setViewerOpen(true);
  };

  const handleCloseQuestionnaire = async () => {
    try {
      setViewerClosing(true);
      await syncSurveyStatus();
      setViewerOpen(false);
      setViewerModule(null);
    } finally {
      setViewerClosing(false);
    }
  };

  return (
    <>
      <div className="marianaGatePage">
        <div className="marianaGatePanel">
          <div className="marianaGateEyebrow">Mariana2026</div>
          <h1 className="marianaGateTitle">Ação Necessária</h1>
          <p className="marianaGateMessage">
            Para continuar utilizando o portal, é necessário completar um
            questionário obrigatório. Este questionário contém informações
            importantes sobre a migração do seu caso para o programa
            Mariana2026.
          </p>
          <p className="marianaGateMessage marianaGateMessageSecondary">
            Enquanto isso, o restante do portal ficará bloqueado até que o
            questionário seja finalizado.
          </p>
          <div className="marianaGateActions">
            <button
              type="button"
              className="marianaGateButton"
              onClick={handleOpenQuestionnaire}
              disabled={!invitationLink || statusLoading}
            >
              {statusLoading ? "Verificando..." : "Abrir questionário"}
            </button>
          </div>
          {!invitationLink ? (
            <div className="marianaGateNote">
              O questionário não está disponível no momento.
            </div>
          ) : null}
        </div>
      </div>

      {viewerOpen ? (
        <PortalSurveyViewer
          module={viewerModule || activeModule}
          onClose={handleCloseQuestionnaire}
          closing={viewerClosing}
        />
      ) : null}
    </>
  );
}
